import React from "react";
import { Helmet } from "react-helmet-async";
import { MapPin, Star, Clock, Users, Phone, Car, Crown, Heart } from "lucide-react";

const MahakaleshwarTemple = () => {
  const templeImage = "/Ujjain/temples/mahakal0.jpg";
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Mahakaleshwar Temple",
    "description": "Sacred Jyotirlinga temple in Ujjain dedicated to Lord Shiva, one of 12 most revered Shiva temples in India",
    "image": templeImage,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Mahakaleshwar Temple Road",
      "addressLocality": "Ujjain",
      "addressRegion": "Madhya Pradesh",
      "addressCountry": "India"
    },
    "openingHours": "Mo-Su 03:00-23:00",
    "priceRange": "Free",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "15000"
    }
  };

  const highlights = [
    {
      icon: <Crown className="w-6 h-6" />,
      title: "Sacred Jyotirlinga",
      description: "One of the 12 most sacred Jyotirlinga temples dedicated to Lord Shiva across India"
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Bhasma Aarti",
      description: "Famous pre-dawn Bhasma Aarti performed with sacred ash, a divine spiritual experience"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Ancient Heritage",
      description: "Centuries-old temple with rich mythology and continuous worship traditions"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Spiritual Significance",
      description: "Powerful spiritual energy and divine presence attracting millions of devotees annually"
    }
  ];

  const visitInfo = [
    { label: "Entry Fee", value: "Free Entry" },
    { label: "Timings", value: "3:00 AM - 11:00 PM (Daily)" },
    { label: "Bhasma Aarti", value: "4:00 AM (Advance booking required)" },
    { label: "Best Time", value: "Mahashivratri, Sawan month" },
    { label: "Duration", value: "2-4 hours" },
    { label: "Facilities", value: "Cloakroom, Prasadam, Wheelchair access" }
  ];

  const templeFeatures = [
    {
      title: "Main Sanctum",
      description: "Sacred Jyotirlinga in underground chamber with natural Shiva Lingam",
      highlight: "Divine darshan experience"
    },
    {
      title: "Bhasma Aarti Hall", 
      description: "Special hall for the famous early morning Bhasma Aarti ceremony",
      highlight: "Spiritual awakening ritual"
    },
    {
      title: "Mahakal Corridor",
      description: "Modern pilgrimage corridor connecting temple to various facilities",
      highlight: "World-class amenities"
    },
    {
      title: "Nandi Mandap",
      description: "Beautiful pavilion housing the sacred Nandi bull facing the main shrine",
      highlight: "Traditional temple architecture"
    },
    {
      title: "Sabha Mandap",
      description: "Assembly hall for religious gatherings and special ceremonies",
      highlight: "Community worship space"
    },
    {
      title: "Temple Museum",
      description: "Collection of ancient artifacts, sculptures, and temple history",
      highlight: "Cultural heritage preservation"
    }
  ];

  const nearbyTemples = [
    { name: "Kal Bhairav Temple", distance: "2 km", time: "8 mins" },
    { name: "Harsiddhi Temple", distance: "3 km", time: "12 mins" },
    { name: "Mangalnath Temple", distance: "4 km", time: "15 mins" },
    { name: "Ram Ghat", distance: "1.5 km", time: "6 mins" }
  ];

  const aartisSchedule = [
    {
      name: "Bhasma Aarti",
      time: "4:00 AM",
      description: "Sacred ash ceremony performed before dawn",
      special: "Advance booking required"
    },
    {
      name: "Madhyan Aarti",
      time: "12:00 PM",
      description: "Midday prayers and offerings to Lord Mahakal",
      special: "Peak devotee time"
    },
    {
      name: "Sandhya Aarti",
      time: "7:00 PM",
      description: "Evening aarti with lamps and devotional songs",
      special: "Most crowded time"
    },
    {
      name: "Shayan Aarti",
      time: "10:30 PM",
      description: "Night prayers before temple closure",
      special: "Peaceful atmosphere"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Mahakaleshwar Temple Ujjain - Sacred Jyotirlinga | Bhasma Aarti | Aaoghumen</title>
        <meta name="description" content="Visit Mahakaleshwar Temple Ujjain with Aaoghumen - experience sacred Jyotirlinga darshan, famous Bhasma Aarti, and divine spiritual energy. Complete guide with timings, booking, and temple significance." />
        <meta name="keywords" content="mahakaleshwar temple ujjain, mahakaleshwar jyotirlinga, bhasma aarti ujjain, mahakal temple timings, ujjain temple booking, aaoghumen mahakaleshwar, lord shiva temple ujjain, jyotirlinga darshan, ujjain spiritual tour" />
        
        <meta property="og:title" content="Mahakaleshwar Temple - Sacred Jyotirlinga Ujjain | Aaoghumen" />
        <meta property="og:description" content="Experience divine darshan at Mahakaleshwar Temple, one of India's 12 sacred Jyotirlingas. Witness the famous Bhasma Aarti and immerse in spiritual energy." />
        <meta property="og:image" content={templeImage} />
        <meta property="og:url" content="https://aaoghumen.com/destinations/ujjain/mahakaleshwar-temple" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Mahakaleshwar Temple - Sacred Jyotirlinga | Ujjain | Aaoghumen" />
        <meta name="twitter:description" content="Discover the divine power of Mahakaleshwar Jyotirlinga and experience the sacred Bhasma Aarti at this ancient Shiva temple." />
        <meta name="twitter:image" content={templeImage} />
        
        <link rel="canonical" href="https://aaoghumen.com/destinations/ujjain/mahakaleshwar-temple" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-r from-orange-600 via-red-500 to-yellow-600 overflow-hidden">
          <img
            src={templeImage}
            alt="Mahakaleshwar Temple Ujjain Jyotirlinga"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-70"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-5xl font-bold mb-4">
                <span className="text-yellow-300">Mahakaleshwar Temple</span> - Sacred Jyotirlinga
              </h1>
              <p className="text-xl mb-6">
                Experience the divine power of one of India's most sacred Jyotirlingas, where Lord Shiva resides as Mahakal, the destroyer of time and death, offering liberation and eternal peace.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  <span>4.8 Rating</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>3 AM - 11 PM</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  <span>Free Entry</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          {/* About Section */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About Mahakaleshwar Temple</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Mahakaleshwar Temple stands as one of India's most revered spiritual destinations, housing one of the twelve sacred Jyotirlingas dedicated to Lord Shiva. Located in the ancient city of Ujjain, this temple has been a center of devotion for over a thousand years, attracting millions of pilgrims who seek the blessings of Mahakal - the lord of time and death. The temple's significance transcends religious boundaries, representing the eternal cycle of creation and destruction that governs the universe.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                The temple is particularly famous for its Bhasma Aarti, a unique pre-dawn ceremony where the deity is adorned with sacred ash from cremated bodies, symbolizing the transcendence of life and death. This ritual, performed in complete darkness before sunrise, creates an atmosphere of profound spirituality that touches the hearts of devotees. The natural Shiva Lingam in the underground sanctum is believed to be self-manifested (Swayambhu), making it even more sacred in Hindu tradition.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                With Aaoghumen, immerse yourself in the divine energy of Mahakaleshwar Temple. Our spiritual guides help you understand the profound significance of the rituals, the temple's rich history, and the philosophical depth of Shaivism. Experience the transformative power of devotion and discover why this sacred site has been a beacon of spiritual enlightenment for countless generations.
              </p>
            </div>
          </section>

          {/* Temple Highlights */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Temple Highlights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {highlights.map((highlight, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="bg-orange-100 p-3 rounded-lg mr-4">
                      {highlight.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{highlight.title}</h3>
                  </div>
                  <p className="text-gray-600">{highlight.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Aarti Schedule */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Daily Aarti Schedule</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {aartisSchedule.map((aarti, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  <h3 className="text-xl font-bold text-orange-600 mb-2">{aarti.name}</h3>
                  <p className="text-2xl font-bold text-gray-800 mb-3">{aarti.time}</p>
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">{aarti.description}</p>
                  <div className="bg-yellow-100 p-2 rounded-lg">
                    <p className="text-yellow-800 font-medium text-xs">{aarti.special}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Temple Features */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Temple Architecture & Features</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templeFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  <h3 className="text-xl font-bold text-orange-600 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">{feature.description}</p>
                  <div className="bg-yellow-100 p-2 rounded-lg">
                    <p className="text-yellow-800 font-medium text-xs">{feature.highlight}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Visit Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Information</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visitInfo.map((info, index) => (
                  <div key={index} className="border-l-4 border-orange-500 pl-4">
                    <h3 className="font-bold text-gray-800 mb-2">{info.label}</h3>
                    <p className="text-gray-600">{info.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Spiritual Experience Guide */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Spiritual Experience Guide</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-orange-600 mb-4">Bhasma Aarti Experience</h3>
                <p className="text-gray-600 mb-4">Witness the sacred pre-dawn ceremony with ash offerings to Lord Mahakal.</p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Book online in advance (limited capacity)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Arrive by 3:30 AM for entry</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>No mobile phones allowed inside</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Dress modestly and remove footwear</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-orange-600 mb-4">Temple Etiquette</h3>
                <p className="text-gray-600 mb-4">Respect the sacred atmosphere and follow temple customs for a blessed experience.</p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Remove shoes before entering temple</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Maintain silence in sanctum area</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Follow queue discipline during peak hours</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>No photography inside main temple</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-orange-600 mb-4">Best Times to Visit</h3>
                <p className="text-gray-600 mb-4">Plan your visit during auspicious times for maximum spiritual benefit.</p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Mahashivratri (most auspicious)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Sawan month (July-August)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Early morning hours (less crowded)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Weekdays (avoid weekend rush)</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Nearby Temples */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Nearby Sacred Sites</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {nearbyTemples.map((temple, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{temple.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-orange-500" />
                      <span>{temple.distance}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Car className="w-4 h-4 mr-2 text-blue-500" />
                      <span>{temple.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How to Reach */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">How to Reach</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Public Transport</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Train:</strong> Ujjain Junction railway station, 2 km from temple</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Bus:</strong> Regular buses from Indore, Bhopal, and other cities</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Auto/Taxi:</strong> Easily available from station and bus stand</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Private Vehicle</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Parking:</strong> Multiple paid parking areas near temple</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Distance:</strong> 55 km from Indore, 200 km from Bhopal</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Route:</strong> Well-connected via NH52 and state highways</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Contact and Plan Visit */}
          <section className="bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Experience Divine Darshan at Mahakaleshwar</h2>
              <p className="text-xl mb-6">Seek blessings of the mighty Mahakal and witness the sacred Bhasma Aarti ceremony</p>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+91 12345-67890</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>Ujjain, Madhya Pradesh</span>
                </div>
              </div>
              <div className="flex justify-center space-x-4">
                <button className="bg-white text-orange-600 font-bold py-3 px-6 rounded-full hover:bg-gray-100 transition-colors">
                  Book Bhasma Aarti
                </button>
                <button className="border-2 border-white text-white font-bold py-3 px-6 rounded-full hover:bg-white hover:text-orange-600 transition-colors">
                  Plan Spiritual Tour
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default MahakaleshwarTemple;